import { tick } from 'svelte';
import { get } from 'svelte/store';
import type { PressHoldInfo, PressHoldOptions } from '@Shelly/shelly-design-system';
import { edit, initialEdit } from 'src/stores/ui/edit_lists';
import {
	isLongPressEditAvailable,
	longPressEditAvailable,
} from 'src/stores/features/dev/long-press-edit';
import { app, os } from 'src/stores/session/native-bridge';
import { hapticFeedback } from 'src/services/native_container_requests_ios';
import { hapticFeedbackAndroid } from 'src/services/native_container_requests_android';

export type EnterListEditFromLongPressOptions = {
	/** Room-devices "pure reorder" path also sets `initialEdit` (matches kebab Reorder room items). */
	initialEdit?: boolean;
};

/**
 * Enter shared rearrange / edit mode from a touch-and-hold shortcut.
 * No-ops when the Dev Modal long-press-edit flag is off or edit is already active.
 */
export const enterListEditFromLongPress = (options: EnterListEditFromLongPressOptions = {}): boolean => {
	if (!isLongPressEditAvailable()) return false;
	if (get(edit)) return false;

	edit.set(true);
	if (options.initialEdit) {
		initialEdit.set(true);
	}

	try {
		if (get(app)) {
			if (get(os) === 'IOS') {
				void hapticFeedback('selection');
			} else if (get(os) === 'ANDROID') {
				void hapticFeedbackAndroid('selection');
			}
		}
	} catch {
		// Native bridge may be unavailable in browser / web builds
	}

	return true;
};

/**
 * svelte-dnd-action only starts a drag from mousedown/touchstart after `dragDisabled`
 * flips. Long-press arms edit mid-gesture, so we synthesize a mousedown on the
 * CardGrid item (usually the pressHold node's parent) once the zone has reconfigured.
 * Window touchmove listeners then follow the same finger.
 */
export const scheduleDndItemPickup = async (info: PressHoldInfo): Promise<void> => {
	const dndItem = info.node.parentElement;
	if (!dndItem) return;

	await tick();
	await new Promise<void>((resolve) => {
		requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
	});

	dndItem.dispatchEvent(
		new MouseEvent('mousedown', {
			bubbles: true,
			cancelable: true,
			clientX: info.clientX,
			clientY: info.clientY,
			screenX: info.clientX,
			screenY: info.clientY,
			button: 0,
			buttons: 1,
			view: typeof window !== 'undefined' ? window : undefined,
		}),
	);
};

export type BuildListPressHoldOptions = EnterListEditFromLongPressOptions & {
	/** Extra gate (e.g. room-devices All Devices route has no reorder). */
	enabled?: boolean;
	/** Called after edit mode is entered so the holder can unlock dragDisabled. */
	onEntered?: () => void;
};

/**
 * Shared `use:pressHold` options for ListMenuCardV2 / device cards.
 * Prefer a reactive `$:` block that rebuilds these when `$edit` / `$longPressEditAvailable` change,
 * because the action reads `enabled` via `update()`.
 */
export const buildListPressHoldOptions = (
	editActive: boolean,
	longPressAvailable: boolean,
	options: BuildListPressHoldOptions = {},
): PressHoldOptions => ({
	enabled: (options.enabled ?? true) && !editActive && longPressAvailable,
	onPressHold: () => {
		if (!enterListEditFromLongPress({ initialEdit: options.initialEdit })) return;
		options.onEntered?.();
	},
	onArmedMove: (info) => {
		void scheduleDndItemPickup(info);
	},
});

/** Re-export for call sites that already import from this helper module. */
export { longPressEditAvailable };
